// Single source of truth for the Interactive Feature Tour.
// Used by:
//   - frontend/src/pages/LearnPage.js          (/learn route)
//   - frontend/src/App.js → FeatureTour section (landing page)
// Never duplicate this data. Add/edit modules here.

const tutorialModules = [
  {
    id: "cycle",
    order: 1,
    title: "Economic Cycle Analysis",
    subtitle: "Know the phase. Know what to hold.",
    short_copy:
      "17 FRED indicators analyzed daily. One canonical phase used across every screen.",
    carousel_slides: [
      "Markets move in 4 phases: Expansion → Peak → Contraction → Recovery",
      "17 FRED economic indicators analyzed and synthesized daily",
      "Every screen in the app reads from the same phase — zero contradictions",
      "Know the phase → know which sectors to hold",
    ],
    cta_text: "See the current phase",
    cta_link: "/market-analysis",
    hashtags: ["#investing", "#economy", "#stockmarket", "#personalfinance"],
    content_types: ["reel", "carousel", "static_image"],
    platform_fit: ["instagram", "tiktok", "linkedin"],
    plan_required: "free",
    screenshots: ["dashboard-cycle-chip", "advanced-cycle-analysis"],
    primary_screenshot: "dashboard-cycle-chip",
    feature_color: "#1D9E75",
  },
  {
    id: "portfolio",
    order: 2,
    title: "Portfolio Builder",
    subtitle: "Build with your actual purchase price.",
    short_copy:
      "Add stocks, ETFs, crypto, indices, and bonds. Enter your real cost basis. See actual P&L from day one.",
    carousel_slides: [
      "Search any stock, ETF, crypto, index, treasury, or commodity",
      "Enter your average cost basis — the price you actually paid",
      "See your real unrealized P&L — not from today's price",
      "Supports Robinhood, eToro, Fidelity, Schwab, IBKR and more",
    ],
    cta_text: "Build your portfolio",
    cta_link: "/portfolio/builder",
    hashtags: ["#portfolio", "#investing", "#stocks", "#ETF", "#costbasis"],
    content_types: ["carousel", "reel", "static_image"],
    platform_fit: ["instagram", "linkedin", "tiktok"],
    plan_required: "free",
    screenshots: [
      "portfolio-builder-search",
      "portfolio-builder-cost-basis",
      "portfolio-created",
    ],
    primary_screenshot: "portfolio-created",
    feature_color: "#1D9E75",
  },
  {
    id: "health_score",
    order: 3,
    title: "Portfolio Health Score",
    subtitle: "One number. Five dimensions.",
    short_copy:
      "Diversification, cycle alignment, concentration, correlation, and drift — scored 0 to 100 and delivered monthly.",
    carousel_slides: [
      "Your Portfolio Health Score: 0 to 100",
      "5 dimensions: Diversification · Alignment · Concentration · Correlation · Drift",
      "Monthly health report delivered to your inbox",
      "Know exactly what to improve — and why",
    ],
    cta_text: "Check your health score",
    cta_link: "/dashboard",
    hashtags: ["#portfolio", "#investing", "#diversification", "#riskmanagement"],
    content_types: ["reel", "carousel", "static_image"],
    platform_fit: ["instagram", "linkedin"],
    plan_required: "free",
    screenshots: [
      "dashboard-health-rings",
      "health-detail-page",
      "health-score-tooltip",
    ],
    primary_screenshot: "health-detail-page",
    feature_color: "#1D9E75",
  },
  {
    id: "cycle_alignment",
    order: 4,
    title: "Cycle Alignment",
    subtitle: "Is your portfolio ready for the current phase?",
    short_copy:
      "See which instruments align with the current cycle, which are neutral, and which work against you.",
    carousel_slides: [
      "In Contraction: Utilities, Healthcare, Consumer Staples outperform",
      "In Expansion: Technology, Financials, Industrials lead",
      "Your Cycle Alignment score: Aligned · Neutral · Misaligned",
      "Updated in real time as the economic phase evolves",
    ],
    cta_text: "Check your alignment",
    cta_link: "/dashboard",
    hashtags: ["#economy", "#investing", "#macro", "#stocks", "#ETF"],
    content_types: ["carousel", "static_image"],
    platform_fit: ["instagram", "linkedin", "tiktok"],
    plan_required: "free",
    screenshots: [
      "portfolio-detail-layer1",
      "cycle-alignment-card",
      "cycle-alignment-accordion",
    ],
    primary_screenshot: "cycle-alignment-card",
    feature_color: "#378ADD",
  },
  {
    id: "evaluator",
    order: 5,
    title: "Instrument Evaluator",
    subtitle: "Evaluate before you buy.",
    short_copy:
      "Any stock, ETF, index, treasury, or crypto — evaluated against the current cycle phase. Metrics adapt to the instrument type.",
    carousel_slides: [
      "Search any instrument: stocks, ETFs, indices, bonds, crypto, commodities",
      "Instant cycle recommendation: Hold · Reduce · Consider",
      "Metrics adapt to the type: stocks ≠ ETFs ≠ bonds ≠ indices",
      "Company profile, sector, industry — all in one place",
    ],
    cta_text: "Evaluate an instrument",
    cta_link: "/market-analysis",
    hashtags: ["#stocks", "#ETF", "#research", "#investing", "#fundamentals"],
    content_types: ["reel", "carousel"],
    platform_fit: ["instagram", "tiktok", "linkedin"],
    plan_required: "premium",
    screenshots: [
      "evaluator-stock-block1",
      "evaluator-stock-block2",
      "evaluator-etf-block2",
      "evaluator-treasury-block2",
      "evaluator-index-block2",
      "evaluator-company-profile",
    ],
    primary_screenshot: "evaluator-stock-block1",
    feature_color: "#BA7517",
  },
  {
    id: "correlation",
    order: 6,
    title: "Correlation Analysis",
    subtitle: "True diversification is about correlation, not just variety.",
    short_copy:
      "See the real relationships between your instruments. Low correlation means real diversification.",
    carousel_slides: [
      "Two assets can look different but move exactly the same",
      "The correlation heatmap reveals hidden concentration",
      "Low correlation = true diversification",
      "Understand what really drives your portfolio's risk",
    ],
    cta_text: "Analyze your correlation",
    cta_link: "/dashboard",
    hashtags: ["#diversification", "#portfolio", "#riskmanagement", "#investing"],
    content_types: ["static_image", "carousel"],
    platform_fit: ["linkedin", "instagram"],
    plan_required: "premium",
    screenshots: ["correlation-heatmap", "correlation-locked"],
    primary_screenshot: "correlation-heatmap",
    feature_color: "#9B59B6",
  },
  {
    id: "rebalancing",
    order: 7,
    title: "Intelligent Rebalancing",
    subtitle: "Rebalance aligned with the current cycle — not just to targets.",
    short_copy:
      "3 rebalancing strategies calculated for the current economic phase. Accept with one click.",
    carousel_slides: [
      "Markets move → your allocations drift from your targets",
      "3 strategies: Sector-based · Performance · Adaptive Momentum",
      "Every suggestion calculated for the current economic phase",
      "Accept recommendations with one click",
    ],
    cta_text: "Rebalance your portfolio",
    cta_link: "/dashboard",
    hashtags: [
      "#rebalancing",
      "#portfolio",
      "#investing",
      "#ETF",
      "#passiveinvesting",
    ],
    content_types: ["reel", "carousel"],
    platform_fit: ["instagram", "linkedin", "tiktok"],
    plan_required: "premium",
    screenshots: ["rebalancing-strategies", "rebalancing-results"],
    primary_screenshot: "rebalancing-results",
    feature_color: "#E24B4A",
  },
  {
    id: "backtesting",
    order: 8,
    title: "Historical Backtesting",
    subtitle: "Test your strategy against real history.",
    short_copy:
      "What would have happened? Test 3 modes against real historical data, including cycle-based rotation.",
    carousel_slides: [
      "Test your portfolio strategy against years of real market data",
      "3 modes: Buy & Hold · Periodic Rebalancing · Cycle-Based Rotation",
      "See returns, max drawdown, and Sharpe ratio",
      "The only backtest that accounts for economic cycle phases",
    ],
    cta_text: "Run a backtest",
    cta_link: "/simulation",
    hashtags: ["#backtesting", "#investing", "#quant", "#portfolio", "#stocks"],
    content_types: ["reel", "carousel"],
    platform_fit: ["instagram", "linkedin", "tiktok"],
    plan_required: "premium",
    screenshots: ["backtest-config", "backtest-results"],
    primary_screenshot: "backtest-results",
    feature_color: "#F39C12",
  },
  {
    id: "ai_builder",
    order: 9,
    title: "AI Portfolio Builder",
    subtitle: "Describe your goals. Get a cycle-aware portfolio.",
    short_copy:
      "Tell the AI what you want in plain language. Get a complete portfolio allocation in seconds, built for the current cycle.",
    carousel_slides: [
      "Describe your goals in plain language — no financial jargon needed",
      "The AI analyzes the current cycle phase and your risk profile",
      "Receive a complete portfolio allocation in seconds",
      "Review, adjust, and save with one click",
    ],
    cta_text: "Build with AI",
    cta_link: "/portfolio/builder",
    hashtags: ["#AI", "#investing", "#portfolio", "#fintech", "#personalfinance"],
    content_types: ["reel", "carousel"],
    platform_fit: ["instagram", "tiktok", "linkedin"],
    plan_required: "premium",
    screenshots: ["ai-builder-input", "ai-builder-result"],
    primary_screenshot: "ai-builder-result",
    feature_color: "#8E44AD",
  },
  {
    id: "alerts",
    order: 10,
    title: "Smart Alerts",
    subtitle: "Know when something important changes.",
    short_copy:
      "Price alerts, portfolio value alerts, cycle phase changes — delivered to your inbox when it matters.",
    carousel_slides: [
      "Set alerts for price movements, portfolio value changes",
      "Get notified when the economic cycle phase changes",
      "Rebalancing alerts when your drift exceeds your threshold",
      "Monthly Portfolio Health Score delivered automatically",
    ],
    cta_text: "Set up alerts",
    cta_link: "/alerts",
    hashtags: ["#investing", "#alerts", "#portfolio", "#stocks", "#passiveinvesting"],
    content_types: ["static_image", "carousel"],
    platform_fit: ["instagram", "linkedin"],
    plan_required: "premium",
    screenshots: ["alerts-center", "alerts-setup"],
    primary_screenshot: "alerts-center",
    feature_color: "#1D9E75",
  },
];

export default tutorialModules;
